import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    getServiceSecrets,
    getUserSecrets,
    upsertServiceSecret,
    upsertUserSecret,
    deleteServiceSecret,
    deleteUserSecret,
} from '../../../api/services';
import {
    getAgentSecrets,
    upsertAgentSecret,
    deleteAgentSecret,
    copyUserSecretsToAgent,
} from '../../../api/agents/agent-services';
import { KeyRound, Edit, AlertCircle, Trash2, Copy } from 'lucide-react';
import Button from '../../common/button/Button';

import styles from './styles/ServiceDetails.module.css';

const SecretEditor = ({ secret, onSave, onCancel }) => {
    const [editedSecret, setEditedSecret] = useState(secret);

    return (
        <div className={styles.blockEditor}>
            <div className={styles.inputGroup}>
                <label>Secret Key:</label>
                <input
                    type='text'
                    value={editedSecret.key}
                    onChange={e => setEditedSecret({...editedSecret, key: e.target.value})}
                    placeholder='Enter secret key'
                    readOnly={true}
                />
            </div>
            <div className={styles.inputGroup}>
                <label>Secret Value:</label>
                <input
                    type='password'
                    value={editedSecret.value}
                    onChange={e => setEditedSecret({...editedSecret, value: e.target.value})}
                    placeholder='Enter secret value'
                />
            </div>
            <div className={styles.editorActions}>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => onSave(editedSecret)}
                    disabled={!editedSecret.key || !editedSecret.value}
                >
                    Save
                </Button>
                <Button 
                    variant="secondary" 
                    size="small" 
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
};

const SecretsList = ({ secrets, onEdit, onDelete }) => {
    if (secrets.length === 0) {
        return <div className={styles.emptyMessage}>No secrets configured</div>;
    }

    return (
        <div className={styles.secretsList}>
            {secrets.map(secret => (
                <div key={secret.key} className={`${styles.secretItem} ${secret.value === '' ? styles.secretItemEmpty : ''}`}>
                    <div className={styles.secretInfo}>
                        <div className={styles.secretKey}>
                            <KeyRound size={14} />
                            {secret.key}
                            {secret.value === '' && (
                                <span className={styles.secretEmptyIndicator}>
                                    <AlertCircle size={14} /> Not configured
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={styles.secretActions}>
                        <Button 
                            variant="secondary" 
                            size="small" 
                            onClick={() => onEdit(secret)}
                        >
                            <Edit size={14} />
                        </Button>
                        {secret.value !== '' && (
                            <Button 
                                variant="danger" 
                                size="small" 
                                onClick={() => onDelete(secret.key)}
                            >
                                <Trash2 size={14} />
                            </Button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

const ServiceSecrets = ({
    serviceId,
    isServiceOwner,
    isAgentContext = false,
    agentId = null,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [serviceSecrets, setServiceSecrets] = useState([]);
    const [userSecrets, setUserSecrets] = useState([]);
    const [editingServiceSecret, setEditingServiceSecret] = useState(null);
    const [editingUserSecret, setEditingUserSecret] = useState(null);

    const loadSecrets = useCallback(async () => {
        try {
            setIsLoading(true);
            if (isServiceOwner) {
                const serviceSecretsData = await getServiceSecrets(serviceId);
                setServiceSecrets(serviceSecretsData);
            }

            if (isAgentContext && agentId) {
                const agentSecretsData = await getAgentSecrets(agentId, serviceId);
                setUserSecrets(agentSecretsData);
            } else {
                const userSecretsData = await getUserSecrets(serviceId);
                setUserSecrets(userSecretsData);
            }
        } catch (error) {
            console.error('Error loading secrets:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isServiceOwner, isAgentContext, agentId]);

    useEffect(() => {
        loadSecrets();
    }, [loadSecrets]);

    // Handlers for service secrets
    const handleSaveServiceSecret = useCallback(async secret => {
        try {
            setIsLoading(true);
            await upsertServiceSecret(serviceId, secret.key, secret.value);
            await loadSecrets();
            setEditingServiceSecret(null);
        } catch (error) {
            console.error('Error saving service secret:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, loadSecrets]);

    const handleDeleteServiceSecret = useCallback(async key => {
        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete the secret "${key}"?`)) {
            return;
        }

        try {
            setIsLoading(true);
            await deleteServiceSecret(serviceId, key);
            await loadSecrets();
        } catch (error) {
            console.error('Error deleting service secret:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, loadSecrets]);

    // Handlers for user/agent secrets
    const handleSaveUserSecret = useCallback(async secret => {
        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                await upsertAgentSecret(agentId, serviceId, secret.key, secret.value);
            } else {
                await upsertUserSecret(serviceId, secret.key, secret.value);
            }

            await loadSecrets();
            setEditingUserSecret(null);
        } catch (error) {
            console.error('Error saving secret:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, loadSecrets, isAgentContext, agentId]);

    const handleDeleteUserSecret = useCallback(async key => {
        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete the secret "${key}"?`)) {
            return;
        }

        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                await deleteAgentSecret(agentId, serviceId, key);
            } else {
                await deleteUserSecret(serviceId, key);
            }

            await loadSecrets();
        } catch (error) {
            console.error('Error deleting secret:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, loadSecrets, isAgentContext, agentId]);

    const handleCopyUserSecrets = useCallback(async () => {
        try {
            setIsLoading(true);
            await copyUserSecretsToAgent(agentId, serviceId);
            await loadSecrets();
        } catch (error) {
            console.error('Error copying user secrets to agent:', error);
            alert(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [agentId, serviceId, loadSecrets]);

    if (isLoading && serviceSecrets.length === 0 && userSecrets.length === 0) {
        return <div className={styles.text}>Loading secrets...</div>;
    }

    return (
        <div className={styles.blockContainer}>
            {/* Service secrets (only for owners) */}
            {isServiceOwner && (
                <div className={styles.blockSection}>
                    <div className={styles.secretsHeader}>
                        <h3 className={styles.secretsTitle}>Service Secrets</h3>
                    </div>

                    <div className={styles.secretDescription}>
                        These secrets are available to all users of this service. Configure required secrets here to make them available to all users.
                        <br />
                        Users will be able to use these secrets, but they cannot view their values.
                        <br />
                        <small className={styles.secretNote}>Note: Only secrets defined in the service's configuration can be used.</small>
                    </div>

                    {editingServiceSecret ? (
                        <SecretEditor
                            secret={editingServiceSecret}
                            onSave={handleSaveServiceSecret}
                            onCancel={() => setEditingServiceSecret(null)}
                        />
                    ) : (
                        <SecretsList
                            secrets={serviceSecrets}
                            onEdit={setEditingServiceSecret}
                            onDelete={handleDeleteServiceSecret}
                        />
                    )}
                </div>
            )}

            {/* User/Agent secrets - for all users, including owners */}
            <div className={styles.blockSection}>
                <div className={styles.secretsHeader}>
                    <h3 className={styles.secretsTitle}>
                        {isAgentContext ? 'Agent Secrets' : 'User Secrets'}
                    </h3>
                    {isAgentContext && (
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={handleCopyUserSecrets}
                            disabled={isLoading}
                        >
                            <Copy size={14} />
                            Copy My Secrets
                        </Button>
                    )}
                </div>

                <div className={styles.secretDescription}>
                    {isAgentContext
                        ? "These secrets are for this agent only. You can configure them individually or copy your existing user secrets."
                        : isServiceOwner
                        ? "These secrets are for your personal use. They won't be shared with other users of the service."
                        : "These secrets are required by the service but not configured by the service owner. Add your personal values here."}
                    <br />
                    <small className={styles.secretNote}>Note: Only secrets defined in the service's configuration can be used.</small>
                </div>

                {editingUserSecret ? (
                    <SecretEditor
                        secret={editingUserSecret}
                        onSave={handleSaveUserSecret}
                        onCancel={() => setEditingUserSecret(null)}
                    />
                ) : (
                    <SecretsList
                        secrets={userSecrets}
                        onEdit={setEditingUserSecret}
                        onDelete={handleDeleteUserSecret}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(ServiceSecrets);
